import { Component, ComponentInterface, Event, EventEmitter, h, Prop, State, Watch } from '@stencil/core';
import { LikertSize, LikertType, LikertValue, LikertVariant, Step } from './jui-likert.model';

@Component({
  tag: 'jui-likert',
  styleUrl: 'jui-likert.scss',
  shadow: true
})
export class JuiLikert implements ComponentInterface {
  /**
   * Internal prop
   */
  @Prop() sb = false;

  /**
   * Likert type: numeric | icon
   */
  @Prop()
  type: LikertType = LikertType.numeric;

  /**
   * Initial selected value for the likert
   */
  @Prop() value: string | LikertValue = '';

  /**
   * Likert color: primary | secondary | success | info | warning | danger | low | high | subtle
   */
  @Prop() color: LikertVariant = LikertVariant.primary;

  /**
   * Progress size: xs | sm | md | lg | xl
   */
  @Prop() size: LikertSize = LikertSize.md;

  /**
   * Minimum value for the scale (minimum 1)
   */
  @Prop() minvalue = 1;

  /**
   * Maximum value for the scale (maximum 10)
   */
  @Prop() maxvalue = 5;

  /**
   * Flag to render the likert in a readonly state
   */
  @Prop() readonly = false;

  /**
   * Likert disabled flag
   */
  @Prop({ attribute: 'disabled', reflect: true })
  disabled: boolean;

  /**
   * Optional data-qa string
   */
  @Prop() dataqastring: string = null;

  @Event()
  valueChange: EventEmitter<LikertValue>;

  @State() private currentValue: LikertValue;
  @State() private dynamicValues: { value: string }[] = [];
  @State() private displayAsColumn = false;

  componentWillLoad(): void {
    if (this.sb) {
      this.initStorybookProps();
    }
    this.generateValues();
    this.setInitialValue();
  }

  @Watch('minvalue')
  @Watch('maxvalue')
  watchValueProps() {
    this.generateValues();
  }

  private setInitialValue() {
    // this.currentValue = this.value;
    this.parseValue(this.value);
  }

  private initStorybookProps(): void {
    if (typeof this.value === 'string') {
      this.value = JSON.parse(this.value);
    }
  }

  private generateValues() {
    // Ensure minValue is at least 1 and at most maxValue - 2
    const min = Math.max(1, Math.min(this.minvalue, this.maxvalue - 2));
    // Ensure maxValue is at least 3 and at most 10
    const max = Math.min(10, Math.max(3, this.maxvalue));

    const values = [];
    for (let i = min; i <= max; i++) {
      values.push({
        value: i.toString()
      });
    }
    this.dynamicValues = values;
    this.displayAsColumn = values.length > 5 && !this.readonly;
  }

  private parseValue(value): void {
    this.currentValue = {
      value: typeof value === 'object' ? value?.value : value,
      max: this.maxvalue
    };
  }

  private onValueChange(value: string) {
    if (this.readonly) {
      return;
    }
    this.parseValue(value);
    if (this.valueChange) {
      this.valueChange.emit(this.currentValue);
    }
  }

  render() {
    const containerClassMap = {
      ['likert']: true,
      [this.color]: true,
      [this.size]: true
    };

    const steps: Step[] = [];

    this.dynamicValues.forEach((item, index) => {
      steps.push({
        index,
        classMap: {
          ['likert__step']: true,
          [this.type]: true,
          ['likert__step--readonly']: this.readonly,
          ['likert__step--selected']: this.currentValue.value === item.value
        },
        value: item.value,
        selected: this.currentValue.value === item.value
      });
    });

    return (
      <div class={containerClassMap} {...(this.dataqastring && { 'data-qa': this.dataqastring })}>
        <div
          class={{
            likert__steps: true,
            'likert__steps--column': this.displayAsColumn
          }}
        >
          {steps.map((step: Step, index: number) => (
            <div
              class={step.classMap}
              onClick={() => this.onValueChange(step.value)}
              {...(this.dataqastring && { 'data-qa': this.dataqastring + '__' + step.value })}
            >
              {!this.displayAsColumn ? (
                <span class={'likert__value'}>{step.value}</span>
              ) : index === 0 ? (
                <span class={'likert__value'}>
                  {step.value}
                  <span class={'likert__value-hint'}>
                    (<slot name={'bottom-hint'}></slot>)
                  </span>
                </span>
              ) : index === steps.length - 1 ? (
                <span class={'likert__value'}>
                  {step.value}
                  <span class={'likert__value-hint'}>
                    (<slot name={'top-hint'}></slot>)
                  </span>
                </span>
              ) : (
                <span class={'likert__value'}>{step.value}</span>
              )}
            </div>
          ))}
        </div>
        {!this.displayAsColumn ? (
          <div class={'likert__hint'}>
            <div>
              <slot name={'bottom-hint'}></slot>
            </div>
            <div>
              <slot name={'top-hint'}></slot>
            </div>
          </div>
        ) : null}
      </div>
    );
  }
}
